
// Imports updated to remove unused
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../stores/authStore";
import { useConfirmDialog } from "../../../components/ConfirmDialog";
import { useProfileModalStore } from "../../../stores/profileModalStore";

interface UserProfileProps {
  planName?: string;
}

export const UserProfile: React.FC<UserProfileProps> = ({
  planName,
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { showDialog } = useConfirmDialog();
  const { openProfileModal } = useProfileModalStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);


  const getUserName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email;
    return t("navigation.profile");
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);



  // Unused handlers removed

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    showDialog({
      title: String(t("navigation.logout")),
      message: String(t("navigation.confirmLogout")),
      confirmText: String(t("navigation.logout")),
      cancelText: String(t("actions.cancel")),
      onConfirm: async () => {
        await logout();
        navigate("/?modal=login");
      },
    });
  };

  return (
    <div
      className="px-3 pb-3 pt-1 mt-auto border-t border-neutral-100 dark:border-neutral-800/40 relative"
      ref={menuRef}
    >
      <div
        className={`flex items-center gap-2.5 cursor-pointer px-2 py-1.5 rounded-lg transition-all duration-300 group active:scale-[0.98] ${
          isMenuOpen 
            ? "bg-neutral-100 dark:bg-neutral-800 shadow-sm" 
            : "hover:bg-neutral-50 dark:hover:bg-neutral-800/40"
        }`}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        <div className="w-7 h-7 bg-gradient-to-br from-accent-primary/10 to-purple-500/10 dark:from-accent-primary/20 dark:to-purple-500/20 rounded-md flex items-center justify-center text-accent-primary border border-accent-primary/20 shadow-sm transition-all group-hover:shadow-md group-hover:scale-105">
          <span 
            className="w-4 h-4" 
            style={{ 
              maskImage: `url(https://cdn.dashbal.com/assets/icon_user_premium.svg)`, 
              WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_user_premium.svg)`, 
              maskRepeat: 'no-repeat', 
              WebkitMaskRepeat: 'no-repeat', 
              maskPosition: 'center', 
              WebkitMaskPosition: 'center', 
              maskSize: 'contain', 
              WebkitMaskSize: 'contain', 
              backgroundColor: 'currentColor', 
              display: 'inline-block' 
            }} 
            role='img' 
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold text-neutral-900 dark:text-neutral-100 truncate leading-tight group-hover:text-accent-primary transition-colors">
            {getUserName()}
          </div>
          <div className="text-[9px] text-neutral-500 dark:text-neutral-400 truncate flex items-center gap-1 mt-0.5">
            <span className="w-1 h-1 rounded-full bg-success-500 shadow-[0_0_3px_rgba(34,197,94,0.4)]"></span>
            <span className="font-medium tracking-tight truncate uppercase opacity-70">{planName || t("profile.noSubscriptions")}</span>
          </div>
        </div>

        <span 
          className={`w-3 h-3 text-neutral-400 dark:text-neutral-500 transition-all duration-300 ${isMenuOpen ? "rotate-180 text-accent-primary" : "group-hover:translate-y-0.5"}`} 
          style={{ 
            maskImage: `url(https://cdn.dashbal.com/assets/icon_3d0bf54a.svg)`, 
            WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3d0bf54a.svg)`, 
            maskRepeat: 'no-repeat', 
            WebkitMaskRepeat: 'no-repeat', 
            maskPosition: 'center', 
            WebkitMaskPosition: 'center', 
            maskSize: 'contain', 
            WebkitMaskSize: 'contain', 
            backgroundColor: 'currentColor', 
            display: 'inline-block' 
          }} 
          role='img' 
        />
      </div>

      {/* Dropdown Menu */}
      <Transition
        as={React.Fragment}
        show={isMenuOpen}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 translate-y-2 scale-95"
        enterTo="transform opacity-100 translate-y-0 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 translate-y-0 scale-100"
        leaveTo="transform opacity-0 translate-y-2 scale-95"
      >
        <div className="absolute left-3 right-3 bottom-full mb-2 bg-white/95 dark:bg-neutral-900/95 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl shadow-neutral-200/40 dark:shadow-neutral-950/80 z-50 overflow-hidden min-w-[200px] backdrop-blur-xl">
          <div className="p-1.5 space-y-0.5">
            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate("/plan");
              }}
              className="w-full px-2 py-1 text-left text-[11px] font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-accent-primary transition-all rounded-lg flex items-center gap-2.5 group/item"
            >
              <div className="w-6 h-6 rounded-md bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center group-hover/item:bg-accent-primary/10 transition-colors">
                <span className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 group-hover/item:text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_7284a925.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_7284a925.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
              </div>
              {t("navigation.upgradePlan")}
            </button>

            <button
              onClick={() => {
                setIsMenuOpen(false);
                openProfileModal();
              }}
              className="w-full px-2 py-1 text-left text-[11px] font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-accent-primary transition-all rounded-lg flex items-center gap-2.5 group/item"
            >
              <div className="w-6 h-6 rounded-md bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center group-hover/item:bg-accent-primary/10 transition-colors">
                <span className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 group-hover/item:text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_14eec959.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_14eec959.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
              </div>
              {t("navigation.settings")}
            </button>

            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate("/docs");
              }}
              className="w-full px-2 py-1 text-left text-[11px] font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-accent-primary transition-all rounded-lg flex items-center gap-2.5 group/item"
            >
              <div className="w-6 h-6 rounded-md bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center group-hover/item:bg-accent-primary/10 transition-colors">
                <span className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 group-hover/item:text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
              </div>
              {t("navigation.help")}
            </button>

            <div className="pt-1.5 mt-1.5 border-t border-neutral-100 dark:border-neutral-800/40">
              <button
                onClick={handleLogoutClick}
                className="w-full px-2 py-1 text-left text-[11px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all rounded-lg flex items-center gap-2.5 group/item"
              >
                <div className="w-6 h-6 rounded-md bg-red-50 dark:bg-red-950/30 flex items-center justify-center group-hover/item:bg-red-100 dark:group-hover/item:bg-red-900/40 transition-colors">
                  <span className="w-3.5 h-3.5 text-red-500 dark:text-red-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3cf72799.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3cf72799.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </div>
                {t("navigation.logout")}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};
